import { Drawer, Pagination, Table } from '@/components/commons';
import Form from '@/components/commons/Form';
import { BannerService } from '@/services/banner';
import { ENDPOINT } from '@/services/endpoint';
import { ImageService } from '@/services/image';
import { IResponse, IResponseError } from '@/types/common';
import { ICreateBannerRequest, IUpdateBannerRequest } from '@/types/request';
import { IUploadImageRequest } from '@/types/request/upload';
import {
  BannerSourceType,
  bannerSourceTypeJP,
  IGetDetailBannerResponse,
  IGetListBannerResponse,
} from '@/types/response';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
  Status,
  statusJP,
  statusOptions,
  statusOptionsFull,
} from '@/utils';
import { ERROR, SUCCESS, SUCCESS_MESSAGE } from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  DatePicker,
  Image,
  Select,
  TableColumnsType,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { BannerForm } from '../Drawers';
import { FORM_NAME } from '../Drawers/BannerForm/constants';
import { formSchemaFn } from '../Drawers/BannerForm/formSchemaFn';
import './styles.scss';

interface IProps {
  title?: string;
}

interface IParams {
  limit: number;
  page: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  status?: string | null;
  startAt?: string | null;
  endAt?: string | null;
}

export const BannerList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailBannerResponse>();
  const [isNotPermissionEdit, setIsNotPermissionEdit] =
    useState<boolean>(false);
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.BANNER.GET_LIST, params],
    () => BannerService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId, isNotPermissionEdit)),
    reValidateMode: 'onChange',
    defaultValues: {
      status: Status.PUBLIC,
    },
  });

  const { handleSubmit, reset, watch, setValue, setError, clearErrors } =
    methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({});
  };

  const handleCreate = async (values: ICreateBannerRequest) => {
    const { attachments, isDisabled, ...rest } = values;
    const payload: ICreateBannerRequest = {
      ...rest,
      image: null,
      attachments: null,
      status: values.status,
    };
    try {
      const response = await ImageService.uploadFile(values?.attachments?.fileList[0]?.originFileObj);
      payload.image = response?.data?.url;
      await createBanner(payload);
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutate: createBanner, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateBannerRequest) => BannerService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.CREATE_BANNER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.BANNER.GET_DETAIL.replace(':id', String(currentId))],
    () => BannerService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailBannerResponse>) => {
        const data = response?.data;
        setDetail(data);
        const payload: ICreateBannerRequest = {
          displayOrder: data?.displayOrder,
          url: data?.url || '',
          image: data?.image?.url,
          startAt: moment(data?.startAt),
          endAt: data?.endAt ? moment(data?.endAt) : null,
          status: data?.status,
          attachments: null,
          sourceType: data?.sourceType,
          sourceId: data?.sourceId,
        };
        reset({
          formName: FORM_NAME.UPDATE_BANNER,
          ...payload,
          prevStatus: data?.status,
          isDisabled: data?.endAt && moment().isAfter(moment(data.endAt)),
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const handleUpdate = async (values: IUpdateBannerRequest) => {
    try {
      if (!detail) throw new Error('Banner not found');
      const { attachments, ...rest } = values;
      const payload: IUpdateBannerRequest = {
        ...rest,
        image: null,
        status: values?.status ? 1 : 0,
        prevStatus: detail.status,
      };
      delete payload.isDisabled;
      delete payload.attachments;
      delete payload.formName;
      if (values?.attachments?.fileList?.length) {
        const response = await ImageService.uploadFile(values?.attachments?.fileList[0]?.originFileObj);
        payload.image = response?.data?.url;
      }
      await updateBanner({ body: payload, id: Number(currentId) });
    } catch (error) {
      console.log({ error });
    }
  };

  const handleDelete = (data: IGetDetailBannerResponse) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      maskClosable: false,
      onOk: () => deleteBanner(currentId!),
      okText: '削除する',
      cancelText: 'キャンセル',
      wrapClassName: 'delete-modal-wrapper',
    });
  };

  const { mutate: updateBanner, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: IUpdateBannerRequest; id: number }) =>
      BannerService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.UPDATE_BANNER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);

        if (error.error === ERROR.BANNER_NOT_FOUND) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const getSortOrder = (columnKey: string) => {
    if (params.sortBy === columnKey) {
      return params.sortDirection === 'ASC'
        ? 'ascend'
        : params.sortDirection === 'DESC'
          ? 'descend'
          : null;
    }
    return null;
  };

  const handleTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any,
  ) => {
    if (sorter && sorter.columnKey && sorter.order) {
      const sortDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
      setParams({
        ...params,
        sortBy: sorter.columnKey,
        sortDirection: sortDirection,
        page: 1,
      });
    } else {
      const { sortBy, sortDirection, ...restParams } = params;
      setParams({
        ...restParams,
        page: 1,
      });
    }
  };

  const { mutate: deleteBanner, isLoading: isLoadingDelete } = useMutation(
    (id: number) => BannerService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.DELETE_BANNER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);

        if (error.error === ERROR.BANNER_RELOAD_DATA) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const columns: TableColumnsType<IGetListBannerResponse> = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: '5%',
      align: 'center',
      sorter: true,
      sortOrder: getSortOrder('id'),
    },
    {
      title: 'バナー画像',
      dataIndex: 'image',
      width: '15%',
      align: 'center',
      render: (image) =>
        image?.url && <Image width={112} src={image?.url} preview={false} />,
    },
    {
      title: '表示順',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: '10%',
      sorter: true,
      align: 'center',
      sortOrder: getSortOrder('displayOrder'),
    },
    {
      title: 'ソースタイプ',
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: '10%',
      align: 'center',
      render: (type: BannerSourceType) => bannerSourceTypeJP[type],
    },
    {
      title: 'タイトル',
      key: 'title',
      width: '15%',
      render: (_, record) => {
        const title =
          record.sourceType === BannerSourceType.ARTICLE && record.article
            ? record.article?.title
            : record.sourceType === BannerSourceType.OFFER && record.offer
              ? record.offer?.title
              : record.sourceId;

        return (
          <Typography.Paragraph
            className='mb-0'
            ellipsis={
              record.article || record.offer
                ? {
                  rows: 2,
                  expandable: true,
                  tooltip: title,
                }
                : false
            }
          >
            {title}
          </Typography.Paragraph>
        );
      },
    },
    {
      title: '公開日時',
      width: '15%',
      render: (value) => {
        return moment(value.startAt).format('YYYY年MM月DD日 HH:mm');
      },
    },
    {
      title: '公開終了日時',
      width: '15%',
      render: (value) => {
        return value.endAt
          ? moment(value.endAt).format('YYYY年MM月DD日 HH:mm')
          : '';
      },
    },
    {
      title: '公開ステータス',
      width: '15%',
      dataIndex: 'status',
      align: 'center',
      render: (value, record) => {
        const isPublic = moment(record.startAt).isSameOrBefore(moment());

        return value === Status.PUBLIC
          ? isPublic
            ? statusJP.PUBLIC
            : statusJP.SCHEDULED
          : statusJP.DRAFT;
      },
    },
  ];

  useEffect(() => {
    if (isNotFoundDetail) {
      setOpenDrawer(false);
      refetch();
    }
  }, [isNotFoundDetail]);

  const handleSelectFilter = (value: string) => {
    setParams({ ...params, page: 1, status: value });
  };

  const handleStartAtChange = (date: any) => {
    if (date && params.endAt) {
      const endAtDate = dayjs(params.endAt);
      if (date.isAfter(endAtDate) || date.isSame(endAtDate)) {
        notificationMessage({
          message: '公開開始日時は公開終了日時より前の日付を選択してください。',
          type: NOTIFY_STATUS.ERROR,
        });
        return;
      }
    }
    setParams({
      ...params,
      page: 1,
      startAt: date ? date.toISOString() : null,
    });
  };

  const handleEndAtChange = (date: any) => {
    if (date && params.startAt) {
      const startAtDate = dayjs(params.startAt);
      if (date.isBefore(startAtDate) || date.isSame(startAtDate)) {
        notificationMessage({
          message: '公開終了日時は公開日時より後の日付を選択してください。',
          type: NOTIFY_STATUS.ERROR,
        });
        return;
      }
    }
    setParams({
      ...params,
      page: 1,
      endAt: date ? date.toISOString() : null,
    });
  };

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <div className='flex justify-between'>
          <div className='flex justify-start gap-[6px] items-center'>
            <Select
              className='select-status-banner'
              allowClear={true}
              defaultValue={null}
              placeholder='公開ステータス'
              onChange={handleSelectFilter}
              options={statusOptions}
            />
            <DatePicker
              className='w-[200px]'
              showTime
              format='YYYY年MM月DD日 HH:mm'
              placeholder='公開日時'
              value={params.startAt ? dayjs(params.startAt) : null}
              onChange={handleStartAtChange}
              allowClear
            />
            <DatePicker
              className='w-[200px]'
              showTime
              format='YYYY年MM月DD日 HH:mm'
              placeholder='公開終了日時'
              value={params.endAt ? dayjs(params.endAt) : null}
              onChange={handleEndAtChange}
              allowClear
            />
            <Typography.Text
              type='secondary'
              className='ml-3 whitespace-nowrap flex items-center gap-1'
            >
              <InfoCircleOutlined className='text-[#84ab42]' />
              該当期間に公開されたイベントを確認します。
            </Typography.Text>
          </div>
          <header className='text-right'>
            <Button
              type='primary'
              onClick={() => {
                setOpenDrawer(true);
                reset({});
                // setValue('status', 0);
                setIsNotFoundDetail(false);
              }}
            >
              トップバナー新規作成
            </Button>
          </header>
        </div>

        <div className='flex justify-between flex-col'>
          <Table
            loading={isFetching || isLoadingUpload}
            scroll={{ x: 800 }}
            bordered
            rowKey='id'
            columns={columns}
            dataSource={data?.data.data}
            pagination={false}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
                setIsNotFoundDetail(false);
              },
              style: {
                cursor: 'pointer',
              },
            })}
            locale={{
              emptyText: 'バナーはまだありません',
            }}
          />

          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <Drawer
        title={!currentId ? 'トップバナー新規作成' : 'トップバナー詳細'}
        onOk={handleSubmit(currentId ? handleUpdate : handleCreate)}
        onCancel={handleCancel}
        onDelete={() => handleDelete(detail!)}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存 ' : '更新 '}
        loading={
          isLoadingUpdate ||
          isLoadingDelete ||
          isFetchingDetail ||
          isLoadingCreate ||
          isLoadingUpload
        }
        isDeleted={!!currentId}
      >
        <Form
          {...methods}
          layout='vertical'
          onSubmit={currentId ? handleUpdate : handleCreate}
        >
          <BannerForm
            isLoading={
              isFetchingDetail ||
              isLoadingUpdate ||
              isLoadingCreate ||
              isLoadingDelete
            }
            openDrawer={openDrawer}
            banner={detail}
          />
        </Form>
      </Drawer>
    </div>
  );
};
